import React, { useContext, useEffect, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Textarea, Field, Text } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { MessageBar } from '@fluentui/react';
import { HelpLink } from '@bingads-webui-react/help-link';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getKeywordInputStyles, messageBarStyle } from './style/keyword-input-style';
import { parseKeywords } from './util/parser';

export const KeywordInput = (props) => {
  const activity = useContext(ActivityContext);
  const classes = useDefaultStyles(getKeywordInputStyles);
  const i18n = useContext(I18nContext);

  const {
    keywordsInput,
    onKeywordInputChanged,
    updateValidation,
    validationResult,
  } = props;

  const showMessageBar = useMemo(() => {
    const { uniqKeywords } = parseKeywords({
      keywordsTextarealVal: keywordsInput,
      i18n,
    });

    return uniqKeywords.length < 20;
  }, [
    keywordsInput,
    i18n,
  ]);

  useEffect(() => {
    const { error, valid } = validationResult;
    updateValidation(!_.isEmpty(error) ? error : [], { custom: valid });
  }, [validationResult, updateValidation]);

  return (
    <>
      {showMessageBar && (
        <MessageBar
          className={classes.messageBar}
          messageBarIconProps={{ iconName: 'Insights' }}
          styles={messageBarStyle}
        >
          {i18n.getString(_TL_('Add 20 or more keywords to improve ad performance.'))}
        </MessageBar>
      )}
      <Field
        label={{
          children: () => (
            <div className={classes.labelContainer}>
              <Text className={classes.text}>{i18n.getString(_TL_('Not sure which match type to use or how to add negative keywords?'))}</Text>
              <HelpLink query="help:app54583" className={classes.helpLink}>
                {i18n.getString(_TL_('Learn more'))}
              </HelpLink>
            </div>
          ),
        }}
        className={classes.filed}
      >
        <Textarea
          placeholder={i18n.getString(_TL_('Enter keywords here (separated by commas, or one keyword per line). Use quotes for phrase match (i.e., "Keyword") or brackets for exact match (i.e., [Keyword]). By default, keywords will use broad match. '))}
          value={keywordsInput}
          onChange={e => onKeywordInputChanged(e.target.value, activity)}
          className={classNames(classes.textArea, 'keyword-input-textarea')}
        />
      </Field>
    </>
  );
};

KeywordInput.propTypes = {
  keywordsInput: PropTypes.string.isRequired,
  onKeywordInputChanged: PropTypes.func.isRequired,
  validationResult: PropTypes.shape({
    error: PropTypes.arrayOf(PropTypes.string).isRequired,
    valid: PropTypes.bool.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
};
